import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { CreditCard, Trash2 } from 'lucide-react';
import { apiPost, apiDel } from '../../lib/api.js';
import { cardLabel, cardExpiry } from './billingFormat.js';
import AddCardForm from './AddCardForm.jsx';
import BillingAddressForm from './BillingAddressForm.jsx';

/**
 * "My Payment Methods": add a card, list/default/delete saved cards, billing address.
 * @param {{overview: object}} props
 */
export default function PaymentMethodsPanel({ overview }) {
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: ['billing-overview'] });
  const cards = overview.cards || [];
  const err = (fallback) => (e) => toast.error(e?.response?.data?.error || fallback);

  const makeDefault = useMutation({
    mutationFn: (id) => apiPost(`/billing-center/payment-methods/${id}/default`, {}),
    onSuccess: () => { toast.success('Default card updated'); refresh(); },
    onError: err('Could not update default card'),
  });
  const remove = useMutation({
    mutationFn: (id) => apiDel(`/billing-center/payment-methods/${id}`),
    onSuccess: () => { toast.success('Card removed'); refresh(); },
    onError: err('Could not remove card'),
  });

  return (
    <section className="panel p-5 space-y-6" data-testid="payment-methods-panel">
      <h2 className="font-display text-xl font-bold flex items-center gap-2"><CreditCard className="w-5 h-5 text-civix-purpleSoft" /> My Payment Methods</h2>

      <AddCardForm onSaved={refresh} />

      <div>
        <div className="grid grid-cols-[1fr_120px_160px_40px] text-sm font-medium border-b border-white/10 pb-2">
          <div>Card</div><div>Expires On</div><div>Actions</div><div />
        </div>
        {cards.length === 0 && <div className="text-sm text-white/40 py-4">No cards saved yet.</div>}
        {cards.map((c) => (
          <div key={c.id} className="grid grid-cols-[1fr_120px_160px_40px] items-center text-sm py-3 border-b border-white/5">
            <div>{cardLabel(c)}</div>
            <div>{cardExpiry(c)}</div>
            <div>
              {c.isDefault
                ? <span className="text-white/60">Default</span>
                : <button type="button" onClick={() => makeDefault.mutate(c.id)} disabled={makeDefault.isPending} className="text-civix-purpleSoft text-xs font-semibold hover:underline">Make default</button>}
            </div>
            <button
              type="button" aria-label={`Remove ${cardLabel(c)}`} disabled={remove.isPending}
              onClick={() => { if (window.confirm(`Remove ${cardLabel(c)}?`)) remove.mutate(c.id); }}
              className="text-white/40 hover:text-red-400"
            ><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>

      <BillingAddressForm address={overview.billingAddress} />
    </section>
  );
}
